<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-6">
      <h3 class="font-weight-bolder text-dark mb-0">Terminais de Venda</h3>
      <button class="btn btn-primary font-weight-bold" @click="abrirModal()">
        Novo Terminal
      </button>
    </div>

    <div v-if="loading" class="d-flex justify-content-center py-10">
      <div class="spinner spinner-primary spinner-lg"></div>
    </div>

    <div v-else-if="terminais.length === 0">
      <div class="card card-custom gutter-b">
        <div class="card-body d-flex flex-column align-items-center py-10">
          <i class="flaticon2-laptop text-muted mb-4" style="font-size: 3rem"></i>
          <h4 class="text-muted font-weight-bold mb-2">
            Nenhum terminal cadastrado
          </h4>
          <p class="text-muted mb-0">
            Clique em "Novo Terminal" para adicionar o primeiro terminal de venda.
          </p>
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div
        v-for="terminal in terminais"
        :key="terminal.uuid_terminal"
        class="col-xl-4 col-lg-6 col-md-12 col-sm-12 mb-4"
      >
        <div class="card card-custom gutter-b h-100">
          <div class="card-body py-4 px-5">

            <!-- Cabeçalho: ícone + nome/uuid + status + ações -->
            <div class="d-flex align-items-start justify-content-between mb-4">
              <div class="d-flex align-items-center" style="min-width: 0">
                <div class="symbol symbol-35 symbol-light-primary mr-3 flex-shrink-0">
                  <span class="symbol-label">
                    <i class="flaticon2-laptop text-primary"></i>
                  </span>
                </div>
                <div style="min-width: 0">
                  <span class="text-dark font-weight-bolder font-size-sm d-block text-truncate">
                    {{ terminal.nome }}
                  </span>
                  <div class="d-flex align-items-center" style="min-width: 0">
                    <span
                      class="text-muted font-size-xs d-block text-truncate"
                      :title="terminal.uuid_terminal"
                    >
                      {{ terminal.uuid_terminal || "—" }}
                    </span>
                    <button
                      v-if="terminal.uuid_terminal"
                      type="button"
                      class="btn btn-clean btn-xs btn-icon ml-1 flex-shrink-0"
                      v-b-tooltip.hover.bottom="'Copiar UUID do terminal'"
                      @click="copiarUuid(terminal.uuid_terminal)"
                    >
                      <i class="far fa-copy text-muted"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="d-flex align-items-center flex-shrink-0 ml-2">
                <span
                  class="badge badge-sm font-size-xs mr-2"
                  :class="terminal.device_id ? 'badge-light-success' : 'badge-light-secondary'"
                >
                  {{ terminal.device_id ? 'Vinculado' : 'Disponível' }}
                </span>
                <button
                  @click="abrirModal(terminal)"
                  class="btn btn-xs btn-icon btn-info btn-hover-info mr-1"
                  v-b-tooltip.hover.bottom="'Editar'"
                >
                  <Pencil :size="14" />
                </button>
                <button
                  @click="excluir(terminal)"
                  class="btn btn-xs btn-icon btn-danger btn-hover-danger"
                  v-b-tooltip.hover.bottom="'Excluir'"
                >
                  <TrashCanIcon :size="14" />
                </button>
              </div>
            </div>

            <!-- Informações de vínculo -->
            <div class="terminal-info-grid">
              <div class="terminal-info-row">
                <span class="terminal-info-label">Computador</span>
                <span class="terminal-info-value">
                  {{ terminal.device_name || 'Nenhum' }}
                </span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Device ID</span>
                <span
                  class="terminal-info-value font-size-xs"
                  style="font-family: monospace"
                  :title="terminal.device_id || ''"
                >
                  {{ terminal.device_id ? terminal.device_id.substring(0, 18) + '…' : '—' }}
                </span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Vinculado em</span>
                <span class="terminal-info-value">{{ _formatarData(terminal.vinculado_em) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Configuração NFC-e</span>
                <span class="terminal-info-value">{{ _descricaoConfiguracaoTerminal(terminal) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Série</span>
                <span class="terminal-info-value">{{ _serieConfiguracaoTerminal(terminal) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Ambiente</span>
                <span class="terminal-info-value">{{ _ambienteConfiguracaoTerminal(terminal) }}</span>
              </div>
              <div class="terminal-info-row">
                <span class="terminal-info-label">Último acesso</span>
                <span class="terminal-info-value">{{ _formatarData(terminal.ultimo_acesso_em) }}</span>
              </div>
            </div>

            <div v-if="currentUser && currentUser.admin" class="mt-3 pt-3 border-top">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary font-weight-bold w-100"
                :disabled="ativacaoLoading"
                @click="gerarAtivacaoPos(terminal)"
              >
                <i class="fas fa-qrcode mr-1"></i>
                {{ terminal.device_id ? "Configurar POS" : "Ativar POS" }}
              </button>
            </div>

            <!-- Botão desvincular (só exibe se houver device_id) -->
            <div v-if="terminal.device_id" class="mt-3 pt-3 border-top">
              <button
                type="button"
                class="btn btn-sm btn-outline-danger font-weight-bold w-100"
                @click="desvincular(terminal)"
              >
                <i class="fas fa-unlink mr-1"></i>
                Desvincular computador
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Modal Novo / Editar Terminal -->
    <b-modal
      id="modal-terminal"
      :title="modalTerminal.uuid_terminal ? 'Editar Terminal' : 'Novo Terminal'"
      centered
      hide-footer
    >
      <b-form @submit.stop.prevent="salvar">
        <b-form-group label="Nome" label-for="modal-nome">
          <b-form-input
            id="modal-nome"
            v-model="modalTerminal.nome"
            :state="modalNomeState"
            placeholder="Nome do terminal"
            autofocus
          />
          <b-form-invalid-feedback>Nome é obrigatório.</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Configuração NFC-e" label-for="modal-config">
          <div v-if="!hasConfiguracaoNfceDisponivel" class="alert alert-warning mb-0">
            <p class="mb-2">
              Nenhuma configuração NFC-e modelo 65 encontrada. Cadastre uma configuração NFC-e antes de criar o terminal.
            </p>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold"
              @click="irParaConfiguracoesNfce"
            >
              Ir para configurações NFC-e
            </button>
          </div>

          <template v-else-if="isConfiguracaoUnica">
            <div class="terminal-config-preview mt-1">
              <span>{{ configuracaoUnicaLabel }}</span>
            </div>
          </template>

          <template v-else>
            <b-form-select
              id="modal-config"
              v-model="modalTerminal.id_configuracao"
              :options="configuracoesOptions"
              :state="modalConfiguracaoState"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  Selecione a configuração NFC-e do terminal
                </b-form-select-option>
              </template>
            </b-form-select>
            <b-form-invalid-feedback>
              Selecione a configuração NFC-e do terminal.
            </b-form-invalid-feedback>
            <div v-if="configuracaoSelecionada" class="terminal-config-preview mt-3">
              <span>{{ _nomeConfiguracao(configuracaoSelecionada) }}</span>
              <span>{{ _ambienteConfiguracao(configuracaoSelecionada) }}</span>
              <span>Série {{ configuracaoSelecionada.serie || '—' }}</span>
              <span>Modelo {{ configuracaoSelecionada.modelo || '—' }}</span>
            </div>
          </template>

          <small class="form-text text-muted mt-2">
            Quando houver apenas uma configuração NFC-e modelo 65, ela será usada automaticamente pelo terminal.
          </small>
        </b-form-group>

        <b-form-group v-if="modalTerminal.uuid_terminal" label="UUID do Terminal" label-for="modal-uuid">
          <b-form-input
            id="modal-uuid"
            v-model="modalTerminal.uuid_terminal"
            disabled
          />
        </b-form-group>

        <div class="d-flex justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-clean font-weight-bold mr-2"
            @click="$bvModal.hide('modal-terminal')"
          >
            Cancelar
          </button>
          <button
            type="submit"
            ref="submit_modal"
            class="btn btn-primary font-weight-bold"
            :disabled="!canSalvarTerminal"
          >
            Salvar
          </button>
        </div>
      </b-form>
    </b-modal>
    <b-modal id="modal-ativacao-pos" title="Ativar POS" centered hide-footer size="lg" scrollable
      :no-close-on-backdrop="ativacaoLoading" :no-close-on-esc="ativacaoLoading" @hidden="limparAtivacao">
      <div v-if="posPreferencias && !ativacaoPos" class="pos-setup">
        <div class="pos-setup__terminal">
          <span class="pos-setup__terminal-icon"><i class="fas fa-cash-register" aria-hidden="true"></i></span>
          <div><h5>{{ posTerminal.nome }}</h5><p>Configure o que estará disponível neste equipamento.</p></div>
          <span class="pos-setup__environment" :class="{ 'is-test': posPreferencias.configuracao_nfce.ambiente === 2 }">
            {{ posPreferencias.configuracao_nfce.ambiente === 2 ? "Homologação" : "Produção" }}
          </span>
        </div>
        <div v-if="posPreferencias.configuracao_nfce.ambiente === 2" class="pos-setup__fiscal-note">
          <i class="fas fa-info-circle" aria-hidden="true"></i> NFC-e de teste, sem valor fiscal. O ambiente é definido na retaguarda.
        </div>
        <section class="pos-setup__section" aria-labelledby="pos-payments-heading">
          <div class="pos-setup__section-heading">
            <div><h6 id="pos-payments-heading">Formas de pagamento</h6><p>Marque as opções que poderão ser usadas no POS.</p></div>
            <span class="pos-setup__count" aria-live="polite">{{ posPagamentos.length }} {{ posPagamentos.length === 1 ? "selecionada" : "selecionadas" }}</span>
          </div>
          <div class="pos-setup__payments">
            <label v-for="payment in posPreferencias.formas_pagamento" :key="payment.id"
              class="pos-payment-option" :class="{ 'is-selected': posPagamentos.includes(Number(payment.id)) }">
              <input type="checkbox" v-model="posPagamentos" :value="Number(payment.id)" :disabled="ativacaoLoading"
                :aria-label="payment.descricao">
              <span class="pos-payment-option__icon"><i :class="posPaymentIcon(payment)" aria-hidden="true"></i></span>
              <span class="pos-payment-option__description">
                <strong>{{ payment.descricao }}</strong>
                <small>{{ Number(payment.vezes || 1) > 1 ? payment.vezes + " parcelas" : "À vista · 1x" }}</small>
              </span>
              <span class="pos-payment-option__check" aria-hidden="true"><i v-if="posPagamentos.includes(Number(payment.id))" class="fas fa-check"></i></span>
            </label>
          </div>
          <p class="pos-setup__hint">Padrão: PIX, crédito 1x, débito 1x e dinheiro, quando cadastrados. Parcelamentos dependem do suporte da adquirente.</p>
        </section>
        <section class="pos-setup__section pos-setup__seller">
          <span class="pos-setup__seller-icon"><i class="far fa-user" aria-hidden="true"></i></span>
          <div>
            <label for="pos-fixed-seller">Vendedor do terminal <span v-if="posPreferencias.vendedor_obrigatorio" class="text-danger">*</span></label>
            <b-form-select v-if="posPreferencias.vendedores.length" id="pos-fixed-seller" v-model="posVendedor"
              :options="posSellerOptions" :disabled="ativacaoLoading" />
            <p v-else>Nenhum vendedor ativo cadastrado. As vendas não terão vendedor.</p>
            <small>Será atribuído a todas as vendas. Uma alteração só será aplicada após sincronizar o POS.</small>
          </div>
        </section>
        <div class="pos-setup__footer">
          <p><i class="fas fa-sync-alt" aria-hidden="true"></i> As configurações entram em vigor na próxima sincronização.</p>
          <div class="pos-setup__actions">
            <button class="btn btn-light" :disabled="ativacaoLoading" @click="$bvModal.hide('modal-ativacao-pos')">Voltar</button>
            <button class="btn btn-primary" :disabled="ativacaoLoading || !posPagamentos.length || (posPreferencias.vendedor_obrigatorio && !posVendedor)"
              @click="salvarPreferenciasPos">
              <i :class="ativacaoLoading ? 'fas fa-spinner fa-spin' : posTerminal.device_id ? 'fas fa-check' : 'fas fa-qrcode'" aria-hidden="true"></i>
              {{ ativacaoLoading ? "Salvando..." : posTerminal.device_id ? "Salvar configurações" : "Salvar e gerar QR Code" }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="ativacaoPos" class="text-center">
        <p class="font-weight-bold">{{ ativacaoPos.terminal_nome }}</p>
        <template v-if="ativacaoSegundos > 0">
          <QrCodeVue :value="ativacaoPos.qr_payload" :size="260" level="M" />
          <p class="mt-4 mb-2">No POS, toque em <strong>Ler QR Code</strong> e aponte para esta tela.</p>
          <p class="text-muted">Uso único. Expira em {{ ativacaoTempo }}.</p>
          <p class="mt-3 mb-1">Código de ativação</p>
          <p><code class="h5 text-break">{{ codigoAtivacaoPos }}</code></p>
          <button class="btn btn-sm btn-light-primary" :disabled="!codigoAtivacaoPos" @click="copiarCodigoAtivacaoPos">
            Copiar código para ativação manual
          </button>
          <p class="text-muted font-size-sm mt-3">A ativação vincula o equipamento à empresa deste terminal.</p>
        </template>
        <p v-else class="text-warning">Código expirado. Feche esta janela e gere outro.</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mapState } from "vuex";
import Pencil from "vue-material-design-icons/Pencil.vue";
import TrashCanIcon from "vue-material-design-icons/TrashCan.vue";
import QrCodeVue from "qrcode.vue";

const emptyTerminal = () => ({ nome: "", uuid_terminal: "", id_configuracao: null });

export default {
  name: "app-terminais-index",
  components: { Pencil, TrashCanIcon, QrCodeVue },
  data() {
    return {
      terminais: [],
      configuracoes: [],
      loading: false,
      modalTerminal: emptyTerminal(),
      modalSubmetido: false,
      ativacaoPos: null,
      ativacaoLoading: false,
      ativacaoAgora: Date.now(),
      ativacaoTimer: null,
      posTerminal: null,
      posPreferencias: null,
      posPagamentos: [],
      posVendedor: null,
    };
  },
  computed: {
    posPaymentOptions() {
      return (this.posPreferencias?.formas_pagamento || []).map(p => ({
        value: Number(p.id), text: p.descricao + " · " + Math.max(1, Number(p.vezes || 1)) + "x",
      }));
    },
    posSellerOptions() {
      return [{ value: null, text: "Selecione o vendedor" },
        ...(this.posPreferencias?.vendedores || []).map(v => ({ value: Number(v.id_vendedor), text: v.nome }))];
    },
    codigoAtivacaoPos() {
      if (!this.ativacaoPos) return "";
      if (typeof this.ativacaoPos.code === "string") return this.ativacaoPos.code;
      try {
        const payload = JSON.parse(this.ativacaoPos.qr_payload);
        return typeof payload.code === "string" ? payload.code : "";
      } catch {
        return "";
      }
    },
    ativacaoSegundos() {
      if (!this.ativacaoPos) return 0;
      return Math.max(0, Math.ceil((Date.parse(this.ativacaoPos.expires_at) - this.ativacaoAgora) / 1000));
    },
    ativacaoTempo() {
      const seconds = this.ativacaoSegundos;
      return Math.floor(seconds / 60) + ":" + String(seconds % 60).padStart(2, "0");
    },
    ...mapGetters(["itsblocked"]),
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),
    modalNomeState() {
      if (!this.modalSubmetido) return null;
      return this.modalTerminal.nome.trim().length > 0 ? true : false;
    },
    modalConfiguracaoState() {
      if (!this.modalSubmetido || !this.precisaSelecionarConfiguracao) return null;
      return !!this.modalTerminal.id_configuracao;
    },
    isNovoTerminalModal() {
      return !this.modalTerminal?.uuid_terminal;
    },
    configuracoesDisponiveisModal() {
      if (!this.isNovoTerminalModal) return this.configuracoes;
      return this.configuracoes.filter((configuracao) =>
        this._isConfiguracaoDisponivelNovoTerminal(configuracao)
      );
    },
    hasConfiguracaoNfceDisponivel() {
      return this.configuracoesDisponiveisModal.length > 0;
    },
    isConfiguracaoUnica() {
      return this.configuracoesDisponiveisModal.length === 1;
    },
    precisaSelecionarConfiguracao() {
      return this.configuracoesDisponiveisModal.length > 1;
    },
    canSalvarTerminal() {
      return this.hasConfiguracaoNfceDisponivel;
    },
    configuracaoUnica() {
      return this.isConfiguracaoUnica ? this.configuracoesDisponiveisModal[0] : null;
    },
    configuracaoUnicaLabel() {
      return this.configuracaoUnica ? this._labelConfiguracao(this.configuracaoUnica) : "";
    },
    configuracoesOptions() {
      return this.configuracoesDisponiveisModal.map((c) => ({
        value: c.id ?? c.id_configuracao,
        text: this._labelConfiguracao(c),
      }));
    },
    configuracaoSelecionada() {
      const id = this.modalTerminal.id_configuracao;
      if (id === null || id === undefined || id === "") return null;
      return this.configuracoesDisponiveisModal.find(
        (c) => String(c.id ?? c.id_configuracao) === String(id)
      ) || null;
    },
  },
  methods: {
    posPaymentIcon(payment) {
      const code = String(payment.tPag || payment.tpag || "").padStart(2, "0");
      return { "01": "fas fa-money-bill-wave", "03": "far fa-credit-card", "04": "fas fa-credit-card",
        "17": "fas fa-qrcode", "20": "fas fa-qrcode" }[code] || "fas fa-wallet";
    },
    async gerarAtivacaoPos(terminal) {
      this.limparAtivacao();
      this.ativacaoLoading = true;
      try {
        const { data } = await this.$http.post("/terminais-venda/pos-configuracao", {
          id_terminal_venda: terminal.id_terminal_venda,
        });
        this.posTerminal = terminal;
        this.posPreferencias = data;
        this.posPagamentos = data.pagamentos_ids.map(Number);
        this.posVendedor = data.vendedor ? Number(data.vendedor.id_vendedor) : null;
        this.$bvModal.show("modal-ativacao-pos");
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.$toast.error(errors?.activation?.[0] || error.response?.data?.message || "Não foi possível gerar a ativação.");
      } finally {
        this.ativacaoLoading = false;
      }
    },
    async salvarPreferenciasPos() {
      if (this.ativacaoLoading || !this.posTerminal) return;
      this.ativacaoLoading = true;
      try {
        await this.$http.post("/terminais-venda/pos-configuracao/salvar", {
          id_terminal_venda: this.posTerminal.id_terminal_venda,
          pagamentos_ids: this.posPagamentos, id_vendedor: this.posVendedor,
        });
        if (this.posTerminal.device_id) {
          this.$toast.success("Preferências salvas. Sincronize o POS para aplicá-las.");
          this.$bvModal.hide("modal-ativacao-pos");
        } else {
          const { data } = await this.$http.post("/terminais-venda/ativacao-pos", {
            id_terminal_venda: this.posTerminal.id_terminal_venda,
          });
          this.ativacaoPos = data;
          this.ativacaoAgora = Date.now();
          this.ativacaoTimer = setInterval(() => { this.ativacaoAgora = Date.now(); }, 1000);
        }
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.$toast.error(errors?.id_vendedor?.[0] || errors?.pagamentos_ids?.[0] || errors?.activation?.[0] || error.response?.data?.message || "Não foi possível salvar as preferências.");
      } finally { this.ativacaoLoading = false; }
    },
    limparAtivacao() {
      if (this.ativacaoTimer) clearInterval(this.ativacaoTimer);
      this.ativacaoTimer = null;
      this.ativacaoPos = null;
      this.posTerminal = null;
      this.posPreferencias = null;
      this.posPagamentos = [];
      this.posVendedor = null;
    },
    async getConfiguracoes() {
      try {
        const { data } = await this.$http.post("/configuracoes/config-nfce");
        const configuracoes = Array.isArray(data) ? data : (data.data ?? []);
        this.configuracoes = configuracoes.filter((configuracao) =>
          this._isConfiguracaoNfceModelo65AtivaDaEmpresa(configuracao)
        );
      } catch {
        this.configuracoes = [];
      }
    },
    async getTerminais() {
      this.loading = true;
      try {
        const { data } = await this.$http.post("/terminais-venda");
        this.terminais = data;
      } catch {
        this.terminais = [];
      } finally {
        this.loading = false;
      }
    },
    async abrirModal(terminal = null) {
      await this.getConfiguracoes();
      this.modalSubmetido = false;
      const idConfiguracaoTerminal =
        terminal?.id_configuracao_nfce ??
        terminal?.id_configuracao ??
        terminal?.configuracao_nfce?.id ??
        terminal?.configuracao_nfce?.id_configuracao ??
        null;

      this.modalTerminal = terminal
        ? {
            ...terminal,
            id_configuracao: idConfiguracaoTerminal,
          }
        : emptyTerminal();

      if (this.isConfiguracaoUnica) {
        this.modalTerminal.id_configuracao =
          this.configuracaoUnica?.id ?? this.configuracaoUnica?.id_configuracao ?? null;
      }

      this.$bvModal.show("modal-terminal");
    },
    async copiarCodigoAtivacaoPos() {
      if (!this.codigoAtivacaoPos || this.ativacaoSegundos <= 0) return;
      await this.copiarUuid(this.codigoAtivacaoPos, "Código de ativação");
    },
    async copiarUuid(uuid, rotulo = "UUID") {
      if (!uuid) return;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(uuid);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = uuid;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "absolute";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }

        this.$toast.success(`${rotulo} copiado para a área de transferência!`);
      } catch {
        this.$toast.error(`Não foi possível copiar o ${rotulo.toLowerCase()}.`);
      }
    },
    async salvar() {
      this.modalSubmetido = true;
      if (!this.modalTerminal.nome.trim()) return;
      if (!this.hasConfiguracaoNfceDisponivel) {
        this.$toast.warning(
          "Nenhuma configuração NFC-e modelo 65 encontrada. Cadastre uma configuração NFC-e antes de criar o terminal."
        );
        return;
      }

      if (this.precisaSelecionarConfiguracao && !this.modalTerminal.id_configuracao) {
        this.$toast.warning("Selecione a configuração NFC-e do terminal.");
        return;
      }

      const idConfiguracaoNfce = this.isConfiguracaoUnica
        ? this.configuracaoUnica?.id ?? this.configuracaoUnica?.id_configuracao ?? null
        : this.modalTerminal.id_configuracao || null;

      const submitButton = this.$refs["submit_modal"];
      submitButton.classList.add("spinner", "spinner-light", "spinner-right");
      submitButton.setAttribute("disabled", "");

      try {
        const url = this.modalTerminal.uuid_terminal
          ? "/terminais-venda/update"
          : "/terminais-venda/create";
        await this.$http.post(url, {
          ...this.modalTerminal,
          id_configuracao_nfce: idConfiguracaoNfce,
        });
        this.$toast.success(
          this.modalTerminal.uuid_terminal
            ? "Terminal atualizado com sucesso!"
            : "Terminal cadastrado com sucesso!"
        );
        this.$bvModal.hide("modal-terminal");
        this.getTerminais();
      } catch {
        submitButton.classList.remove("spinner", "spinner-light", "spinner-right");
        submitButton.removeAttribute("disabled");
      }
    },
    excluir(terminal) {
      if (terminal.device_id) {
        this.$swal.fire({
          icon: "error",
          title: "Terminal vinculado",
          text: `O terminal "${terminal.nome}" está vinculado ao computador "${terminal.device_name || terminal.device_id}". Desvincule o computador antes de excluir.`,
          confirmButtonText: "Entendido",
        });
        return;
      }

      this.$swal
        .fire({
          icon: "warning",
          title: "Excluir terminal?",
          text: `Deseja excluir o terminal "${terminal.nome}"?`,
          showCancelButton: true,
          confirmButtonText: "Sim, excluir",
          cancelButtonText: "Cancelar",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              await this.$http.post("/terminais-venda/delete", {
                id_terminal_venda: terminal.id_terminal_venda,
              });
              this.$toast.success("Terminal excluído com sucesso!");
              this.getTerminais();
            } catch {
              // tratado pelo interceptor do axios
            }
          }
        });
    },

    desvincular(terminal) {
      this.$swal
        .fire({
          icon: "warning",
          title: "Desvincular computador?",
          html:
            `<p>Tem certeza que deseja desvincular o terminal <strong>${terminal.nome}</strong> ` +
            `do computador <strong>${terminal.device_name || terminal.device_id}</strong>?</p>` +
            `<p class="text-muted font-size-sm mt-2">` +
            `Após isso, o PDV Desktop instalado nesse computador não poderá mais operar ` +
            `com este terminal quando estiver online, e o terminal ficará disponível ` +
            `para ser configurado em outro computador.</p>`,
          showCancelButton: true,
          confirmButtonText: "Sim, desvincular",
          confirmButtonColor: "#f64e60",
          cancelButtonText: "Cancelar",
        })
        .then(async (result) => {
          if (!result.isConfirmed) return;
          try {
            await this.$http.post(
              `/terminais-venda/${terminal.id_terminal_venda}/desvincular-device`
            );
            this.$toast.success("Terminal desvinculado com sucesso!");
            // Atualiza o terminal na lista localmente para feedback imediato
            const idx = this.terminais.findIndex(
              (t) => t.id_terminal_venda === terminal.id_terminal_venda
            );
            if (idx !== -1) {
              this.$set(this.terminais, idx, {
                ...this.terminais[idx],
                device_id:   null,
                device_name: null,
                vinculado_em: null,
              });
            }
          } catch {
            // tratado pelo interceptor do axios
          }
        });
    },

    _nomeConfiguracao(config) {
      if (!config) return "Padrão do PDV Web";
      return config.descricao || config.nome || config.nome_configuracao || `Configuração #${config.id || config.id_configuracao || "—"}`;
    },

    _labelConfiguracao(config) {
      const partes = [
        this._nomeConfiguracao(config),
        `Série ${config?.serie || "—"}`,
        this._ambienteConfiguracao(config),
      ];
      if (config?.padrao_pdv_web === true) {
        partes.push("Padrão PDV Web");
      }
      return partes.join(" — ");
    },

    _isConfiguracaoNfceModelo65AtivaDaEmpresa(configuracao) {
      const modelo = Number(configuracao?.modelo);
      const ativoRaw = configuracao?.ativo;
      const ativo = !(ativoRaw === false || ativoRaw === 0 || ativoRaw === "0");

      const idEmpresaConfig = Number(
        configuracao?.id_empresa ??
          configuracao?.empresa_id ??
          this.currentUser?.empresa?.id_empresa ??
          this.currentUser?.usuario_empresa?.id_empresa ??
          0
      );
      const idEmpresaAtual = Number(
        this.currentUser?.empresa?.id_empresa ??
          this.currentUser?.usuario_empresa?.id_empresa ??
          0
      );
      const mesmaEmpresa = idEmpresaAtual === 0 || idEmpresaConfig === 0 || idEmpresaConfig === idEmpresaAtual;

      return modelo === 65 && ativo && mesmaEmpresa;
    },

    _isConfiguracaoDisponivelNovoTerminal(configuracao) {
      return configuracao?.padrao_pdv_web !== true;
    },

    irParaConfiguracoesNfce() {
      this.$bvModal.hide("modal-terminal");
      this.$router.push({ name: "configuracoesNovo" });
    },

    _ambienteConfiguracao(config) {
      const ambiente = config?.ambiente;
      if (String(ambiente) === "1") return "Produção";
      if (String(ambiente) === "2") return "Homologação";
      return "Ambiente —";
    },

    _configuracaoDoTerminal(terminal) {
      const config = terminal?.configuracao_nfce || terminal?.configuracao || null;
      if (config) return config;
      const id = terminal?.id_configuracao_nfce ?? terminal?.id_configuracao ?? null;
      return this.configuracoes.find((c) => String(c.id) === String(id)) || null;
    },

    _descricaoConfiguracaoTerminal(terminal) {
      const config = this._configuracaoDoTerminal(terminal);
      return config ? this._nomeConfiguracao(config) : "Padrão do PDV Web";
    },

    _serieConfiguracaoTerminal(terminal) {
      const config = this._configuracaoDoTerminal(terminal);
      return config?.serie || terminal?.serie || terminal?.serie_nfe || "—";
    },

    _ambienteConfiguracaoTerminal(terminal) {
      const config = this._configuracaoDoTerminal(terminal);
      return config ? this._ambienteConfiguracao(config) : "Padrão";
    },

    _formatarData(dateStr) {
      if (!dateStr) return "—";
      try {
        return new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(dateStr));
      } catch {
        return dateStr;
      }
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }
    this.getConfiguracoes();
    this.getTerminais();
  },
  beforeDestroy() {
    this.limparAtivacao();
  },
};
</script>

<style scoped>
.pos-setup { color: #263c48; padding: 4px 8px; }
.pos-setup__terminal { display: flex; align-items: center; gap: 14px; padding: 8px 0 18px; }
.pos-setup__terminal-icon { width: 50px; height: 50px; display: flex; align-items: center; justify-content: center; background: #eaf5f1; border-radius: 14px; flex-shrink: 0; }
.pos-setup__terminal-icon i { color: #257c6b; font-size: 21px; }
.pos-setup h5, .pos-setup h6 { margin: 0; color: #213746; font-weight: 700; }
.pos-setup h5 { font-size: 18px; }
.pos-setup h6 { font-size: 15px; }
.pos-setup p { margin: 5px 0 0; color: #6d7b87; font-size: 13px; line-height: 1.5; }
.pos-setup__environment { margin-left: auto; border-radius: 20px; padding: 6px 11px; background: #eaf0fa; color: #355580; font-size: 12px; font-weight: 600; white-space: nowrap; }
.pos-setup__environment.is-test { background: #fff3dc; color: #92621e; }
.pos-setup__fiscal-note { border-radius: 10px; padding: 10px 12px; background: #fffaf0; color: #87652e; font-size: 12px; line-height: 1.6; margin-bottom: 18px; }
.pos-setup__fiscal-note i { color: inherit; font-size: 13px; margin-right: 5px; }
.pos-setup__section { border: 1px solid #e5ebef; border-radius: 14px; padding: 18px; margin-bottom: 16px; }
.pos-setup__section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.pos-setup__count { background: #f2f6f8; color: #586c79; border-radius: 16px; padding: 5px 9px; font-size: 11px; white-space: nowrap; }
.pos-setup__payments { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; max-height: 318px; overflow-y: auto; padding: 2px; }
.pos-payment-option { display: flex; align-items: center; gap: 10px; border: 1px solid #dfe7ed; border-radius: 11px; padding: 13px 12px; margin: 0; cursor: pointer; min-height: 78px; position: relative; transition: border-color .15s, background .15s; }
.pos-payment-option:hover { border-color: #7bada2; background: #f8fbfa; }
.pos-payment-option.is-selected { background: #eff8f4; border-color: #3d917d; }
.pos-payment-option:focus-within { outline: 2px solid #318d78; outline-offset: 2px; }
.pos-payment-option input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.pos-payment-option__icon { width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; background: #f0f3f6; flex-shrink: 0; }
.pos-payment-option__icon i { font-size: 17px; color: #637887; }
.is-selected .pos-payment-option__icon { background: #deefe7; }
.is-selected .pos-payment-option__icon i { color: #247761; }
.pos-payment-option__description { min-width: 0; flex: 1; }
.pos-payment-option__description strong { display: block; font-size: 13px; font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
.pos-payment-option__description small { display: block; margin-top: 5px; color: #73828c; font-size: 11px; }
.pos-payment-option__check { width: 19px; height: 19px; border: 1px solid #cbd7df; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #fff; }
.is-selected .pos-payment-option__check { background: #287d66; border-color: #287d66; }
.pos-payment-option__check i { color: #fff; font-size: 10px; }
.pos-setup .pos-setup__hint { margin-top: 13px; font-size: 11px; color: #84929c; }
.pos-setup__seller { display: flex; align-items: flex-start; gap: 12px; }
.pos-setup__seller > div { flex: 1; min-width: 0; }
.pos-setup__seller-icon { padding-top: 4px; }
.pos-setup__seller-icon i { color: #528071; font-size: 18px; }
.pos-setup__seller label { display: block; font-size: 14px; font-weight: 600; margin-bottom: 10px; }
.pos-setup__seller small { display: block; color: #7e8c96; font-size: 11px; margin-top: 9px; line-height: 1.5; }
.pos-setup__footer { border-top: 1px solid #edf1f4; padding: 12px 0 4px; position: sticky; bottom: -20px; background: #fff; z-index: 2; box-shadow: 0 -8px 18px #ffffffed; }
.pos-setup__footer p { font-size: 11px; margin-bottom: 14px; }
.pos-setup__footer p i { color: #6b9285; font-size: 11px; margin-right: 4px; }
.pos-setup__actions { display: flex; justify-content: flex-end; gap: 10px; }
.pos-setup__actions .btn { border-radius: 9px; padding: 11px 17px; }
@media (max-width: 480px) {
  .pos-setup { padding: 0; }
  .pos-setup__payments { grid-template-columns: minmax(0, 1fr); }
  .pos-setup__terminal { flex-wrap: wrap; gap: 10px; }
  .pos-setup__environment { margin-left: 0; }
  .pos-setup__section { padding: 13px; }
  .pos-setup__actions .btn { flex: 1; padding: 11px 8px; }
}
.terminal-info-grid {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.terminal-info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.terminal-info-label {
  color: #b5b5c3;
  white-space: nowrap;
  flex-shrink: 0;
}

.terminal-info-value {
  color: #3f4254;
  font-weight: 500;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 65%;
}

.terminal-config-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.terminal-config-preview span {
  background: #f3f6f9;
  border-radius: 4px;
  color: #3f4254;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.25rem 0.45rem;
}
</style>
